import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Scan, Package, MapPin, CheckCircle, AlertCircle, User, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const FGPutAway = () => {
  const navigate = useNavigate();
  const staffInputRef = useRef(null);
  const usnInputRef = useRef(null);
  const binInputRef = useRef(null);

  // Staff state
  const [staffId, setStaffId] = useState('');
  const [staffValidated, setStaffValidated] = useState(false);
  const [staffName, setStaffName] = useState('');
  
  // USN state
  const [usnCode, setUsnCode] = useState('');
  const [usnDetails, setUsnDetails] = useState(null);
  
  // Bin state
  const [suggestedBin, setSuggestedBin] = useState(null);
  const [binCode, setBinCode] = useState('');
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [putAwaySuccess, setPutAwaySuccess] = useState(false);
  const [sessionCount, setSessionCount] = useState(0);

  useEffect(() => {
    staffInputRef.current?.focus();
  }, []);

  const handleStaffValidate = async () => {
    if (!staffId.trim()) {
      toast.error('Please enter Staff ID');
      return;
    }

    try {
      setLoading(true);
      const response = await apiClient.get(`/validate/staff/${staffId}`);
      if (response.data.valid) {
        setStaffValidated(true);
        setStaffName(response.data.staff_name);
        toast.success(`Staff validated: ${response.data.staff_name}`);
        setTimeout(() => usnInputRef.current?.focus(), 100);
      } else {
        toast.error('Invalid Staff ID');
      }
    } catch (error) {
      toast.error('Staff validation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleUsnScan = async () => {
    if (!usnCode.trim()) {
      toast.error('Please scan USN barcode');
      return;
    }

    try {
      setLoading(true);
      // Validate USN for FG put-away
      const response = await apiClient.post(`/wms/fg/validate-usn/${usnCode}`);
      
      setUsnDetails(response.data.usn);
      
      if (response.data.suggested_bin) {
        setSuggestedBin(response.data.suggested_bin);
      }
      
      // Keep bin location input blank - user must manually enter/scan
      setBinCode('');
      
      toast.success(`USN validated. Suggested bin: ${response.data.suggested_bin?.bin_code || 'None'}`);
      setTimeout(() => binInputRef.current?.focus(), 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'USN validation failed');
      setUsnCode('');
      usnInputRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPutAway = async () => {
    if (!binCode.trim()) {
      toast.error('Please enter bin location');
      return;
    }

    try {
      setLoading(true);
      const response = await apiClient.post('/wms/fg/put-away', {
        staff_id: staffId,
        usn_code: usnCode,
        bin_code: binCode
      });

      setPutAwaySuccess(true);
      setSessionCount(prev => prev + 1);
      toast.success(`Pack ${usnCode} put away to ${binCode}`);
      
      // Reset for next USN after 2 seconds
      setTimeout(() => {
        resetForNextUsn();
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Put-away failed');
    } finally {
      setLoading(false);
    }
  };

  const resetForNextUsn = () => {
    setUsnCode('');
    setUsnDetails(null);
    setSuggestedBin(null);
    setBinCode('');
    setPutAwaySuccess(false);
    setTimeout(() => usnInputRef.current?.focus(), 100);
  };

  const resetAll = () => {
    setStaffId('');
    setStaffValidated(false);
    setStaffName('');
    setUsnCode('');
    setUsnDetails(null);
    setSuggestedBin(null);
    setBinCode('');
    setPutAwaySuccess(false);
    setSessionCount(0);
    setTimeout(() => staffInputRef.current?.focus(), 100);
  };

  const handleKeyPress = (e, action) => {
    if (e.key === 'Enter') {
      action();
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      {/* Header */}
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/wms')}
              className="text-[#F9FAFB] hover:bg-white/10 mr-4"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">FG Put-Away</h1>
              <p className="text-sm text-[#E8DCC8]">Individual Pack Storage</p>
            </div>
          </div>
          {staffValidated && (
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm text-[#E8DCC8]">Session Packs</p>
                <p className="text-xl font-bold text-emerald-400">{sessionCount}</p>
              </div>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-8">
        {/* Info Card */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex gap-3">
            <Package className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">FG Put-Away Process (Each Pack)</p>
              <ol className="list-decimal list-inside space-y-1">
                <li>Validate Staff ID</li>
                <li>Scan individual USN/Pack barcode</li>
                <li>Confirm or change suggested bin location</li>
                <li>Pack is stored in FG storage</li>
              </ol>
              <p className="mt-2 text-xs text-blue-600">
                <strong>Zone Rules:</strong> Zone A/B = 1 SKU per bin | Zone C = Multiple SKUs allowed
              </p>
            </div>
          </div>
        </div>

        {/* Success Banner */}
        {putAwaySuccess && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-green-600" />
            <div>
              <p className="font-medium text-green-800">Put-Away Successful!</p>
              <p className="text-sm text-green-600">Pack {usnCode} stored at {binCode}</p>
            </div>
          </div>
        )}

        <div className="space-y-6">
          {/* Step 1: Staff Validation */}
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${staffValidated ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'}`}>
                {staffValidated ? <CheckCircle className="w-5 h-5" /> : <User className="w-5 h-5" />}
              </div>
              <h2 className="text-lg font-semibold text-[#1A1F2E]">Step 1: Staff Validation</h2>
            </div>
            
            {!staffValidated ? (
              <div className="flex gap-4">
                <div className="flex-1">
                  <Label htmlFor="staffId">Staff ID</Label>
                  <Input
                    ref={staffInputRef}
                    id="staffId"
                    value={staffId}
                    onChange={(e) => setStaffId(e.target.value)}
                    onKeyPress={(e) => handleKeyPress(e, handleStaffValidate)}
                    placeholder="Scan or enter Staff ID"
                    className="mt-1"
                    data-testid="fg-staff-input"
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    onClick={handleStaffValidate}
                    disabled={loading || !staffId}
                    className="bg-[#2D5F4F] hover:bg-[#234A3D]"
                  >
                    Validate
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">{staffName}</span>
                  <span className="text-sm text-gray-500">({staffId})</span>
                </div>
                <Button variant="ghost" size="sm" onClick={resetAll}>
                  Change Staff
                </Button>
              </div>
            )}
          </div>

          {/* Step 2: Scan USN */}
          {staffValidated && (
            <div className="bg-white rounded-lg border border-[#E5DFD0] p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${usnDetails ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'}`}>
                  {usnDetails ? <CheckCircle className="w-5 h-5" /> : <Scan className="w-5 h-5" />}
                </div>
                <h2 className="text-lg font-semibold text-[#1A1F2E]">Step 2: Scan USN (Individual Pack)</h2>
              </div>

              {!usnDetails ? (
                <div className="flex gap-4">
                  <div className="flex-1">
                    <Label htmlFor="usnCode">USN / Pack Barcode</Label>
                    <Input
                      ref={usnInputRef}
                      id="usnCode"
                      value={usnCode}
                      onChange={(e) => setUsnCode(e.target.value)}
                      onKeyPress={(e) => handleKeyPress(e, handleUsnScan)}
                      placeholder="Scan pack barcode"
                      className="mt-1 font-mono"
                      data-testid="fg-usn-input"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button
                      onClick={handleUsnScan}
                      disabled={loading || !usnCode}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Scan className="w-4 h-4 mr-2" />
                      Validate
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* USN and Bundle Info Row */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500">USN Code</p>
                      <p className="font-mono font-medium">{usnDetails.usn_code}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500">SKU / Barcode</p>
                      <p className="font-medium truncate" title={usnDetails.item_barcode}>{usnDetails.item_barcode || '-'}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500">Velocity Class</p>
                      <span className={`inline-flex px-2 py-1 rounded text-sm font-bold ${
                        usnDetails.velocity_class === 'A' ? 'bg-green-100 text-green-700' :
                        usnDetails.velocity_class === 'B' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {usnDetails.velocity_class || 'C'}
                      </span>
                    </div>
                  </div>
                  
                  {/* Product Details Row */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                      <p className="text-xs text-blue-600 font-medium">Design Name</p>
                      <p className="font-medium text-blue-900 truncate" title={usnDetails.design_name}>{usnDetails.design_name || '-'}</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-3 border border-purple-100">
                      <p className="text-xs text-purple-600 font-medium">Shade</p>
                      <p className="font-medium text-purple-900 truncate" title={usnDetails.shade}>{usnDetails.shade || '-'}</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                      <p className="text-xs text-green-600 font-medium">Size</p>
                      <p className="font-medium text-green-900">{usnDetails.size || '-'}</p>
                    </div>
                    {usnDetails.bundle_code && (
                      <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
                        <p className="text-xs text-amber-600 font-medium">Bundle</p>
                        <p className="font-mono font-medium text-amber-900 truncate" title={usnDetails.bundle_code}>{usnDetails.bundle_code}</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Description Row (if available) */}
                  {usnDetails.description && (
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <p className="text-xs text-gray-500 font-medium">Description</p>
                      <p className="text-sm text-gray-700">{usnDetails.description}</p>
                    </div>
                  )}
                  
                  <Button variant="outline" size="sm" onClick={resetForNextUsn}>
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Scan Different USN
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Bin Location */}
          {usnDetails && (
            <div className="bg-white rounded-lg border border-[#E5DFD0] p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-full flex items-center justify-center bg-purple-100 text-purple-600">
                  <MapPin className="w-5 h-5" />
                </div>
                <h2 className="text-lg font-semibold text-[#1A1F2E]">Step 3: Confirm Bin Location</h2>
              </div>

              {suggestedBin && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-3">
                    <MapPin className="w-6 h-6 text-green-600" />
                    <div>
                      <p className="text-sm text-green-600">Suggested Bin</p>
                      <p className="text-2xl font-bold text-green-800">{suggestedBin.bin_code}</p>
                    </div>
                    <div className="ml-auto text-right text-sm">
                      <p className="text-green-600">Capacity</p>
                      <p className="font-medium">{suggestedBin.current_qty || 0}/{suggestedBin.capacity || 100}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-4">
                <div className="flex-1">
                  <Label htmlFor="binCode">Bin Location</Label>
                  <Input
                    ref={binInputRef}
                    id="binCode"
                    value={binCode}
                    onChange={(e) => setBinCode(e.target.value)}
                    onKeyPress={(e) => handleKeyPress(e, handleConfirmPutAway)}
                    placeholder="Scan or enter bin location"
                    className="mt-1 font-mono"
                    data-testid="fg-bin-input"
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    onClick={handleConfirmPutAway}
                    disabled={loading || !binCode}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="fg-confirm-putaway"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Confirm Put-Away
                  </Button>
                </div>
              </div>

              {/* Zone Rules Reminder */}
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>Zone Rules:</strong> Zone A/B bins accept only 1 SKU. Zone C bins can hold multiple SKUs.
                </p>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default FGPutAway;
